import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { NavLink, useNavigate } from "react-router-dom";
import CustomPersonIcon from "../../assets/sellerNavbarIcons/ProfileIcon_SellerNavbar.svg";
import arrowDown from "../../assets/sellerNavbarIcons/arrowDown.svg";
import OutletIcon from "../../assets/outlets.svg";
import LogoutIcon from "../../assets/sellerNavbarIcons/logout.svg";
import Analytics from "../../assets/sellerNavbarIcons/analytics.svg";
import FeedbackIcon from "../../assets/sellerNavbarIcons/feedback.svg";
import { useAppDispatch } from "../../redux/hooks";
import { reset } from "../../redux/features/slices/appSlice";
import { useRole } from "../../context/RoleContext";
import { routes } from "../../routes/routes";
import { useQueryClient } from "@tanstack/react-query";
import { logoutWithBackend } from "../../api/loginAPI";
import { AuthApiService } from "../../service/authService";
import colors from "../../theme/Colours";

const NavContainer = styled(AppBar)(() => ({
  backgroundColor: "#fff",
  color: "#000",
  boxShadow: "20px",
}));

const NavItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  minHeight: "20px",
}));

const IconStyle = {
  width: "20px",
  height: "20px",
  opacity: 1,
};

type SellerNavbarProps = {
  logo?: string;
};

const logoStyle = {
  height: "30px",
  cursor: "pointer",
  margin: "11.5px 55px 11.5px 0px",
};

const SalesRepNavbar: React.FC<SellerNavbarProps> = ({ logo }) => {
  const orgId = localStorage.getItem("orgId");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [userName] = useState<string>(localStorage.getItem("Name") || "");
  const { role } = useRole();
  const queryClient = useQueryClient();
  const NAV_ITEMS = [
    {
      label: "Dashboard",
      to: "dashboard",
      icon: Analytics,
      style: { width: "17px", height: "17px" },
    },
    {
      label: "Assigned Outlets",
      to: "assigned-outlets",
      icon: OutletIcon,
    },
    {
      label: userName,
      icon: CustomPersonIcon,
      subMenu: [
        {
          label: "Feedback",
          action: "feedback",
          icon: FeedbackIcon,
        },
        {
          label: "Logout",
          action: "logout",
          icon: LogoutIcon,
        },
      ],
    },
  ];

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logoutWithBackend()
      .catch((err) => console.error("Logout failed:", err))
      .finally(() => {
        AuthApiService.logout()
          .catch((err) => console.error("Logout failed:", err))
          .finally(() => {
            dispatch(reset());
            queryClient.clear();
            navigate(`/${routes.public.signIn}`, {
              replace: true,
              state: { fromApp: true },
            });
          });
      });
  };

  const handleFeedback = () => {
    window.open("https://tally.so/r/J9epXR", "_blank");
    handleMenuClose();
  };

  return (
    <NavContainer position="fixed">
      <Toolbar>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Logo Section */}
          <Box>
            <NavLink to={`/${role}/dashboard`}>
              {!logo ? (
                <Skeleton variant="rectangular" sx={logoStyle} />
              ) : (
                <img src={logo} alt="Logo" style={logoStyle} />
              )}
            </NavLink>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              ml: 8,
            }}
          >
            {NAV_ITEMS.filter((item) => !item.subMenu).map(
              ({ label, to, icon, style }) => (
                <NavLink
                  key={label}
                  to={`${role}/${to}`}
                  style={{ textDecoration: "none" }}
                >
                  {({ isActive }) => (
                    <NavItem
                      sx={{
                        padding: "7px 13px",
                        borderRadius: "20px",
                        backgroundColor: isActive ? "#F97316" : "transparent",
                        color: isActive ? "#fff" : "#111827",
                        transition: "all 0.2s ease",
                        boxShadow: isActive
                          ? "0 4px 10px rgba(255, 106, 0, 0.25)"
                          : "none",
                        "&:hover": {
                          backgroundColor: isActive
                            ? colors.primaryButtonOrange
                            : "rgba(255, 106, 0, 0.08)",
                          textDecoration: "none",
                        },
                      }}
                    >
                      <img
                        src={icon}
                        alt={label}
                        style={{
                          ...IconStyle,
                          ...style,
                          filter: isActive ? "brightness(0) invert(1)" : "none",
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: isActive ? "#fff" : "#000",
                        }}
                      >
                        {label}
                      </Typography>
                    </NavItem>
                  )}
                </NavLink>
              )
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", mr: 4 }}>
            {NAV_ITEMS.filter((item) => item.subMenu).map(
              ({ label, icon, subMenu }) => (
                <Box key={label}>
                  <IconButton
                    onClick={handleProfileMenuOpen}
                    color="inherit"
                    disableRipple
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <img
                      src={icon}
                      alt={label}
                      style={{ width: "16px", height: "16px" }}
                    />
                    <Typography
                      sx={{
                        paddingX: "12px",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </Typography>
                    <img src={arrowDown} />
                  </IconButton>
                  <Menu
                    data-testid="user-menu"
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    sx={{
                      ".css-1toxriw-MuiList-root-MuiMenu-list": {
                        width: "100px",
                        padding: "4px 10px",
                        outline: 0,
                        fontFamily: "Poppins, sans-serif",
                        borderRadius: "10px",
                        border: "1px solid #64748B",
                      },
                      ".MuiPaper-root": {
                        boxShadow: "none",
                      },
                      ".MuiMenuItem-root": {
                        padding: "8px 10px",
                        "&:hover": {
                          borderRadius: "4px",
                        },
                      },
                    }}
                  >
                    {subMenu?.map(({ label, icon, action }, index) => (
                      <React.Fragment key={label}>
                        <MenuItem
                          onClick={() => {
                            if (action === "logout") {
                              handleLogout();
                            } else if (action === "feedback") {
                              handleFeedback();
                            }
                          }}
                        >
                          <img
                            src={icon}
                            alt={label}
                            style={{
                              width: "16px",
                              height: action === "logout" ? "14px" : "16px",
                              paddingRight: "8px",
                            }}
                          />
                          <Typography sx={{ fontSize: "14px" }}>
                            {label}
                          </Typography>
                        </MenuItem>
                        {index < subMenu.length - 1 && (
                          <Divider sx={{ margin: "4px 0" }} />
                        )}
                      </React.Fragment>
                    ))}
                  </Menu>
                </Box>
              )
            )}
          </Box>
        </Box>
      </Toolbar>
    </NavContainer>
  );
};

export default SalesRepNavbar;
